import { useLocation, useNavigate } from "react-router-dom";
import { plus } from "../../asset";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import PageHeader from "../../components/PageHeader";
import { popupNavigation } from "../../util/functions/UniversalFunctions";
import AddStudentsList from "./AddStudentsList";
import AddedStudentList from "./AddedStudentList";
import SubButton from "../../components/SubButtons";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  setPagination,
  setSearchFilters,
} from "../../features/student/StudentSlice";
import { toast } from "sonner";
import GroupApis from "../../api/groupApis";
import { useBranchCode } from "../../util/hooks/useBranch";
import ClassSelect from "../../components/common-input/ClassSelect";
import AcademicCycleSelect from "../../components/common-input/AcademicCycle";
import { IS_LIVE } from "../../util/config/env";
import SelectStaffModal from "../../components/modal/AddTeachersToGroup";
import { AddStaffForm, Staff } from "../../types/staff";
import { clearGroupDetails } from "../../features/group-houses/GroupSlice";

type SelectedStaff = {
  staffId: number;
  fullName: string;
};

export default function AddGroup() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [showStaffModal, setShowStaffModal] = useState(false);

  const mode = location.state?.mode ?? "create";
  const groupData = location.state?.group;
  const isEdit = mode === "edit";

  const [groupName, setGroupName] = useState("");
  // const [selectedClassId, setSelectedClassId] = useState<number | "">("");
  // const [selectedCycleId, setSelectedCycleId] = useState<number | "">("");
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<SelectedStaff[]>([]);
  const [refreshGroupStudents, setRefreshGroupStudents] = useState(0);
  const branchCode = useBranchCode();

  const { groupDetails } = useSelector((state: RootState) => state.group);

  const handleSave = async () => {
    if (!groupName.trim()) {
      toast.error("Group name is required");
      return;
    }

    if (!branchCode) {
      toast.error("Branch not found");
      return;
    }

    try {
      let response;

      if (isEdit && groupData?.groupId) {
        response = await GroupApis.updateGroup(
          groupData.groupId,
          groupName,
          branchCode,
          selectedStudentIds,
          selectedStaffIds,
        );
      } else {
        response = await GroupApis.createGroup(
          groupName,
          branchCode,
          selectedStudentIds,
          selectedStaffIds,
        );
      }

      if (response?.status === 1) {
        if (isEdit) {
          setRefreshGroupStudents((prev) => prev + 1);
        } else {
          setGroupName("");
          setSelectedStudentIds([]);
          setSelectedStaff([]);
          setSelectedStaffIds([]);
        }
        // setSelectedClassId("");
        // setSelectedCycleId("");
        dispatch(setSearchFilters({ classId: "", academicCycleId: "" }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const pageTitle =
    mode === "edit" ? "EDIT GROUP / HOUSE" : "CREATE NEW GROUP / HOUSE";

  const { searchFilters } = useSelector((state: RootState) => state.student);

  const handleFilterChange = (name: string, value: string) => {
    dispatch(setSearchFilters({ [name]: value }));
    // Reset to page 0 whenever a filter changes
    dispatch(setPagination({ currentPage: 0, pageSize: 10 }));
  };

  useEffect(() => {
    if (isEdit && groupDetails) {
      setGroupName(groupDetails.groupName || "");

      const mappedStaff = (groupDetails.staffs || []).map((staff) => ({
        staffId: staff.staffId,
        fullName: staff.staffName,
      }));

      setSelectedStaff(mappedStaff);
      setSelectedStaffIds(mappedStaff.map((s) => s.staffId));

      const studentIds =
        groupDetails.students?.map((s) => s.admissionNumber) || [];

      setSelectedStudentIds(studentIds);
    }
  }, [groupDetails, isEdit]);

  useEffect(() => {
    if (!isEdit) {
      dispatch(clearGroupDetails());
    }
  }, [isEdit]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <PageHeader showBack title={pageTitle} />

        <CustomButton
          color="bg-primary-500"
          text={isEdit ? "Update" : "Save"}
          onClick={handleSave}
        />
      </div>

      <div className="bg-white px-6 py-5 rounded-lg flex flex-col gap-9">
        <CustomInput
          name="groupName"
          label="Group/House Name"
          type="text"
          placeholder="Enter the name of the group or house"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          inputClassName="md:w-[50%]"
        />

        <div className="flex gap-4 items-center">
          <div className="flex gap-4 items-center">
            <span className="text-neutral-400 text-nowrap text-sm font-semibold">
              Add Teachers
            </span>
            <SubButton
              text="Click to select"
              icon={plus}
              padding="px-4"
              color="bg-success-700"
              onClick={() => {
                setShowStaffModal(true);
              }}
            />
            {selectedStaff.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedStaff.map((teacher) => (
                  <div
                    key={teacher.staffId}
                    className="flex items-center gap-2 px-3 py-[6px] bg-success-700 text-white rounded text-[13px]"
                  >
                    <span>{teacher.fullName}</span>

                    <button
                      type="button"
                      className="text-white font-bold"
                      onClick={() => {
                        setSelectedStaff((prev) =>
                          prev.filter((s) => s.staffId !== teacher.staffId),
                        );
                        setSelectedStaffIds((prev) =>
                          prev.filter((id) => id !== teacher.staffId),
                        );
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:flex justify-center items-center lg:max-w-3xl lg:mx-auto gap-4">
        <ClassSelect
          name="classId"
          value={searchFilters?.classId}
          placeholder="Filter by class name..."
          onChange={(e) => handleFilterChange("classId", e.target.value)}
          selectClassName="border-none bg-white"
          includeAll
        />

        <AcademicCycleSelect
          name="academicCycleId"
          value={searchFilters.academicCycleId}
          placeholder="Filter by Academic Cycle..."
          includeAll
          onChange={(e) =>
            handleFilterChange("academicCycleId", e.target.value)
          }
          selectClassName="border-none bg-white"
        />

        {!IS_LIVE && (
          <CustomButton
            text="Import"
            color="bg-success-500"
            onClick={() => navigate("/all-students/student-data-import")}
          />
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-2">
        <AddStudentsList
          groupId={groupData?.groupId}
          isEdit={isEdit}
          selectedStudentIds={selectedStudentIds}
          setSelectedStudentIds={setSelectedStudentIds}
        />
        <AddedStudentList
          groupId={groupData?.groupId}
          refreshKey={refreshGroupStudents}
          selectedStudentIds={selectedStudentIds}
          setSelectedStudentIds={setSelectedStudentIds}
        />
      </div>

      {showStaffModal && (
        <SelectStaffModal
          multiple={true}
          title="Select Teachers"
          actionLabel="Add"
          onClose={() => setShowStaffModal(false)}
          defaultSelectedIds={selectedStaffIds}
          onAction={(staff) => {
            const normalizedStaff = staff
              .filter(
                (
                  s,
                ): s is Required<Pick<AddStaffForm, "staffId" | "fullName">> =>
                  typeof s.staffId === "number" &&
                  typeof s.fullName === "string",
              )
              .map((s) => ({
                staffId: s.staffId,
                fullName: s.fullName,
              }));

            setSelectedStaff(normalizedStaff);
            setSelectedStaffIds(normalizedStaff.map((s) => s.staffId));
          }}
        />
      )}
    </div>
  );
}
